import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Coins, Heart, Package, Search, Shield, Star, Trophy, X, Zap } from 'lucide-react';
import { GenericCard } from '@/components/molecules/GenericCard';
import { PlayerStatsPanel } from '@/components/molecules/PlayerStatsPanel';
import { CustomButton } from '@/components/molecules/CustomButton';
import { Loader } from '@/components/atoms/Loader';
import {
  equipHeldItem,
  getMyTeam,
  getOwnedPokemons,
  getPokePlayerStats,
  releasePokemon,
  removeHeldItem,
  setTeamSlot as setTeamSlotService,
  toggleFavorite as toggleFavoriteService,
  useSuperBonbon,
  type OwnedPokemon,
  type HeldItem,
  type TeamSlot,
  type EquipHeldItemResponse,
  type EvolutionData,
  type PendingMoveData,
} from '@/services/pokemon';
import { getInventory, type InventoryItem } from '@/services/user';
import { EvolutionModal } from '@/components/molecules/EvolutionModal';
import { LearnMoveModal } from '@/components/molecules/LearnMoveModal';
import { cdnUrl } from '@/utils/cdn';
import { TYPE_COLORS } from '@/utils/pokemonTypes';
import { useToast } from '@/hooks/useToastContext';

const STAT_LABELS: Record<string, string> = {
  pv: 'PV',
  attack: 'Attaque',
  defense: 'Défense',
  attackspe: 'Atk Sp.',
  defensespe: 'Déf Sp.',
  speed: 'Vitesse',
};

const CATEGORY_LABELS: Record<string, string> = {
  physical: 'Physique',
  special: 'Spécial',
  status: 'Statut',
};

const IV_KEYS: { key: keyof OwnedPokemon; label: string }[] = [
  { key: 'ivHp', label: 'PV' },
  { key: 'ivAtk', label: 'Atk' },
  { key: 'ivDef', label: 'Déf' },
  { key: 'ivSpAtk', label: 'Atk Sp.' },
  { key: 'ivSpDef', label: 'Déf Sp.' },
  { key: 'ivSpe', label: 'Vit.' },
];

const TOTAL_POKEMON = 1214;
const MAX_STAT = 255;

const RARITY_REWARDS: Record<number, number> = { 0: 20, 1: 100, 2: 500, 3: 1000, 4: 5000 };

interface PokemonDetailModalProps {
  pokemon: OwnedPokemon;
  onClose: () => void;
}

const PokemonDetailModal = ({ pokemon, onClose }: PokemonDetailModalProps) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [localHeldItem, setLocalHeldItem] = useState<HeldItem | null>(pokemon.heldItem);
  const [localFavorite, setLocalFavorite] = useState(pokemon.isFavorite);
  const [localTeamSlot, setLocalTeamSlot] = useState<number | null>(pokemon.teamSlot);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [evolutionResult, setEvolutionResult] = useState<{ fromName: string; evolvedName: string; isNewInPokedex: boolean; pokeDollarsEarned: number } | null>(null);
  const [pendingEvolution, setPendingEvolution] = useState<EvolutionData | undefined>(undefined);
  const [pendingMove, setPendingMove] = useState<PendingMoveData | undefined>(undefined);

  const { data: inventoryData, isLoading: isInventoryLoading } = useQuery({
    queryKey: ['inventory-picker'],
    queryFn: () => getInventory({ limit: 500 }),
    staleTime: 5 * 60_000,
  });

  const { data: myTeamData } = useQuery({
    queryKey: ['myTeam'],
    queryFn: getMyTeam,
    staleTime: 30_000,
  });

  const { mutate: mutateFavorite, isPending: isTogglingFavorite } = useMutation({
    mutationFn: () => toggleFavoriteService(pokemon.ownedPokemonId),
    onSuccess: (result) => {
      setLocalFavorite(result.isFavorite);
      queryClient.invalidateQueries({ queryKey: ['pokemons'] });
    },
    onError: (err: Error) => showToast('error', err.message),
  });

  const { mutate: mutateTeamSlot, isPending: isSettingTeamSlot } = useMutation({
    mutationFn: (slot: number | null) => setTeamSlotService(pokemon.ownedPokemonId, slot),
    onSuccess: (result) => {
      setLocalTeamSlot(result.teamSlot);
      queryClient.invalidateQueries({ queryKey: ['pokemons'] });
      queryClient.invalidateQueries({ queryKey: ['myTeam'] });
    },
    onError: (err: Error) => showToast('error', err.message),
  });

  const SUPER_BONBON_ID = 37;

  const usableItems = (inventoryData?.data ?? []).filter(
    (item: InventoryItem) =>
      item.itemType !== 'ticket' &&
      item.itemType !== 'region_ticket' &&
      item.quantity > 0
  );

  const invalidateAfterUse = () => {
    queryClient.invalidateQueries({ queryKey: ['pokemons'] });
    queryClient.invalidateQueries({ queryKey: ['inventory-picker'] });
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
  };

  const { mutate: equip, isPending: isEquipping } = useMutation({
    mutationFn: (item: InventoryItem) => equipHeldItem(pokemon.ownedPokemonId, item.itemId),
    onSuccess: (result: EquipHeldItemResponse) => {
      setShowItemPicker(false);
      invalidateAfterUse();
      if (result.action === 'equipped') {
        setLocalHeldItem(result.heldItem);
      } else if (result.action === 'evolved') {
        setLocalHeldItem(null);
        setEvolutionResult({ fromName: result.fromName, evolvedName: result.evolvedName, isNewInPokedex: result.isNewInPokedex, pokeDollarsEarned: result.pokeDollarsEarned });
      } else if (result.action === 'ribbon_applied') {
        showToast('success', `Ruban Fée appliqué\u00A0! +${result.happinessGained} bonheur (total\u00A0: ${result.newHappiness})`);
      }
    },
    onError: (err: Error) => showToast('error', err.message),
  });

  const { mutate: useBonbon, isPending: isUsingBonbon } = useMutation({
    mutationFn: () => useSuperBonbon(pokemon.ownedPokemonId),
    onSuccess: (result) => {
      setShowItemPicker(false);
      invalidateAfterUse();
      showToast('success', `Niveau ${result.newLevel} atteint\u00A0!`);
      if (result.evolution) setPendingEvolution(result.evolution);
      else if (result.pendingMove) setPendingMove(result.pendingMove);
    },
    onError: (err: Error) => showToast('error', err.message),
  });

  const handleItemUse = (item: InventoryItem) => {
    if (item.itemId === SUPER_BONBON_ID) useBonbon();
    else equip(item);
  };

  const isItemPending = isEquipping || isUsingBonbon;

  const { mutate: remove, isPending: isRemoving } = useMutation({
    mutationFn: () => removeHeldItem(pokemon.ownedPokemonId),
    onSuccess: () => {
      setLocalHeldItem(null);
      invalidateAfterUse();
    },
    onError: (err: Error) => showToast('error', err.message),
  });

  const { mutate: release, isPending: isReleasing } = useMutation({
    mutationFn: () => releasePokemon(pokemon.ownedPokemonId),
    onSuccess: (data) => {
      showToast(
        'success',
        `${pokemon.nickname} relâché\u00A0! +${data.pokeDollarsEarned.toLocaleString('fr-FR')} PokéDollars (solde\u00A0: ${data.newBalance.toLocaleString('fr-FR')})`
      );
      queryClient.invalidateQueries({ queryKey: ['pokemons'] });
      queryClient.invalidateQueries({ queryKey: ['mePlayerStats'] });
      onClose();
    },
    onError: (err: Error) => showToast('error', err.message),
  });

  const reward = RARITY_REWARDS[pokemon.rarity] ?? 20;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        className="bg-surface relative w-full max-w-lg overflow-y-auto rounded-3xl shadow-2xl"
        style={{ maxHeight: '90dvh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-secondary-500 sticky top-0 z-10 rounded-t-3xl px-6 pt-6 pb-5">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/40 transition-colors hover:text-white"
          >
            <X size={20} />
          </button>
          <button
            type="button"
            onClick={() => mutateFavorite()}
            disabled={isTogglingFavorite}
            aria-label={localFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            className="absolute top-4 right-12 text-white/40 transition-colors hover:text-white disabled:opacity-50"
          >
            <Star size={20} className={localFavorite ? 'fill-yellow-400 text-yellow-400' : ''} />
          </button>
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-white/10">
              <img
                src={cdnUrl(
                  pokemon.isShiny ? pokemon.pokemon.spriteAnimationShiny : pokemon.pokemon.spriteAnimation
                )}
                alt={pokemon.nickname}
                className="h-16 w-16 object-contain"
                onError={(e) => { e.currentTarget.style.opacity = '0'; }}
              />
            </div>
            <div>
              <p className="font-lexend text-xs text-white/40">
                #{String(pokemon.pokemon.pokedexId).padStart(3, '0')}
              </p>
              <div className="flex items-center gap-2">
                <h2 className="text-white-neutral font-lexend text-xl font-semibold">
                  {pokemon.nickname}
                </h2>
                {pokemon.isShiny && <span className="text-sm text-yellow-400">★</span>}
              </div>
              <p className="text-sm text-white/60">{pokemon.pokemon.realname}</p>
              <div className="mt-2 flex gap-1.5">
                {pokemon.pokemon.types.map((typeName) => (
                  <span
                    key={typeName}
                    className={`${TYPE_COLORS[typeName] ?? 'bg-gray-400'} rounded px-2 py-0.5 text-xs font-semibold text-white capitalize`}
                  >
                    {typeName}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-5 grid grid-cols-3 gap-3 text-center">
            {[
              { label: 'Niveau', value: pokemon.level },
              { label: 'Taille', value: `${pokemon.pokemon.height} m` },
              { label: 'Poids', value: `${pokemon.pokemon.weight} kg` },
            ].map(({ label, value }) => (
              <div key={label} className="bg-primary-500/10 rounded-xl p-3">
                <p className="text-content/50 text-xs">{label}</p>
                <p className="text-content font-lexend font-semibold">{value}</p>
              </div>
            ))}
          </div>

          <div className="mb-3">
            <div className="mb-1 flex justify-between text-xs">
              <span className="text-content/60 font-semibold">Points de vie</span>
              <span className="text-content font-semibold">
                {pokemon.hp} / {pokemon.hpMax}
              </span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-raised">
              <div
                className="h-2.5 rounded-full bg-green-400 transition-all"
                style={{ width: `${Math.round((pokemon.hp / pokemon.hpMax) * 100)}%` }}
              />
            </div>
          </div>

          <div className="mb-5">
            <div className="mb-1 flex justify-between text-xs">
              <span className="text-content/60 font-semibold">Bonheur</span>
              <span className="text-content font-semibold">
                {pokemon.happiness} / 255
              </span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-raised">
              <div
                className="h-2.5 rounded-full bg-pink-400 transition-all"
                style={{ width: `${Math.round((pokemon.happiness / 255) * 100)}%` }}
              />
            </div>
          </div>

          <div className="mb-5">
            <p className="text-content font-lexend mb-3 text-sm font-semibold">
              Statistiques de base
            </p>
            <div className="flex flex-col gap-2">
              {Object.entries(pokemon.pokemon.stats)
                .filter(([k]) => k !== 'total')
                .map(([key, val]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-content/50 w-16 shrink-0 text-right text-xs">
                      {STAT_LABELS[key] ?? key}
                    </span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-raised">
                      <div
                        className="from-primary-500 to-secondary-500 h-2 rounded-full bg-linear-to-r transition-all"
                        style={{ width: `${Math.round((val / MAX_STAT) * 100)}%` }}
                      />
                    </div>
                    <span className="text-content w-8 text-right text-xs font-semibold">
                      {val}
                    </span>
                  </div>
                ))}
              <div className="flex items-center gap-2 border-t border-line-soft pt-1">
                <span className="text-content/70 w-16 shrink-0 text-right text-xs font-semibold">
                  Total
                </span>
                <div className="flex-1" />
                <span className="text-content w-8 text-right text-xs font-bold">
                  {pokemon.pokemon.stats.total}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-5">
            <p className="text-content font-lexend mb-3 text-sm font-semibold">
              Valeurs individuelles (IVs)
            </p>
            <div className="grid grid-cols-3 gap-2">
              {IV_KEYS.map(({ key, label }) => {
                const val = pokemon[key] as number;
                const pct = Math.round((val / 31) * 100);
                const color =
                  val >= 28 ? 'text-green-600' : val >= 15 ? 'text-primary-500' : 'text-content/50';
                return (
                  <div key={key} className="bg-primary-500/10 rounded-xl p-2.5 text-center">
                    <p className="text-content/50 text-xs">{label}</p>
                    <p className={`font-lexend text-sm font-semibold ${color}`}>
                      {val}
                      <span className="text-content/30 text-[10px]">/31</span>
                    </p>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-raised">
                      <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-5">
            <p className="text-content font-lexend mb-3 text-sm font-semibold">
              Attaques
            </p>
            <div className="grid grid-cols-2 gap-2">
              {Array.from({ length: 4 }).map((_, i) => {
                const atk = pokemon.attacks?.[i];
                if (!atk) {
                  return (
                    <div
                      key={i}
                      className="flex h-16 items-center justify-center rounded-xl border-2 border-dashed border-line-soft"
                    >
                      <span className="text-content/25 text-xs">—</span>
                    </div>
                  );
                }
                return (
                  <div
                    key={atk.id}
                    className="flex flex-col gap-1 rounded-xl bg-surface p-2.5 shadow-sm"
                  >
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`${TYPE_COLORS[atk.type] ?? 'bg-gray-400'} shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold text-white capitalize`}
                      >
                        {atk.type}
                      </span>
                      <span className="text-content font-lexend truncate text-xs font-semibold">
                        {atk.realname}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-content/50">
                        {CATEGORY_LABELS[atk.category]}
                      </span>
                      <span className="text-content/30">·</span>
                      <span className="text-content/70">
                        Puissance&nbsp;{atk.power ?? '—'}
                      </span>
                      <span className="text-content/30">·</span>
                      <span className="text-content/70">
                        {atk.accuracy !== null ? `${atk.accuracy}%` : '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Item tenu */}
          <div className="mb-5">
            <p className="text-content font-lexend mb-3 text-sm font-semibold">
              Item tenu
            </p>
            {localHeldItem ? (
              <div className="flex items-center gap-3 rounded-2xl border border-line-soft bg-surface p-3 shadow-sm">
                <div className="bg-primary-500/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
                  <img src={localHeldItem.icon} alt={localHeldItem.name} className="h-7 w-7 object-contain" onError={(e) => { e.currentTarget.style.opacity = '0'; }} />
                </div>
                <p className="text-content flex-1 text-sm font-medium">{localHeldItem.name}</p>
                <CustomButton
                  title="Retirer"
                  color="secondary"
                  variant="ghost"
                  size="sm"
                  rounded="full"
                  loading={isRemoving}
                  onClick={() => remove()}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => setShowItemPicker((v) => !v)}
                  className="text-primary-500 hover:text-primary-600 flex items-center gap-1.5 text-sm font-semibold"
                >
                  <Package size={14} />
                  {showItemPicker ? 'Fermer' : 'Équiper un item'}
                </button>
                {showItemPicker && (
                  <div className="max-h-48 overflow-y-auto rounded-2xl border border-line-soft bg-surface shadow-sm">
                    {isInventoryLoading ? (
                      <div className="flex justify-center py-6">
                        <Loader size={24} color="#f29602" />
                      </div>
                    ) : usableItems.length === 0 ? (
                      <p className="text-content/50 px-4 py-6 text-center text-sm">
                        Aucun item disponible
                      </p>
                    ) : (
                      <div className={isItemPending ? 'pointer-events-none opacity-60' : undefined}>
                        {usableItems.map((item) => (
                          <button
                            key={item.itemId}
                            type="button"
                            onClick={() => handleItemUse(item)}
                            className="hover:bg-primary-500/10 flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors first:rounded-t-2xl last:rounded-b-2xl"
                          >
                            {item.icon ? (
                              <img src={item.icon} alt={item.name} className="h-7 w-7 object-contain" onError={(e) => { e.currentTarget.style.opacity = '0'; }} />
                            ) : (
                              <div className="h-7 w-7 shrink-0" />
                            )}
                            <span className="text-content text-sm font-medium">{item.name}</span>
                            <span className="text-content/40 ml-auto text-xs">×{item.quantity}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Équipe */}
          <div className="mb-5">
            <p className="text-content font-lexend mb-3 text-sm font-semibold">
              Équipe
            </p>
            {localTeamSlot !== null ? (
              <div className="flex items-center justify-between rounded-2xl border border-line-soft bg-surface p-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-primary-500" />
                  <p className="text-content text-sm font-medium">Slot {localTeamSlot}</p>
                </div>
                <CustomButton
                  title="Retirer"
                  color="secondary"
                  variant="ghost"
                  size="sm"
                  rounded="full"
                  loading={isSettingTeamSlot}
                  onClick={() => mutateTeamSlot(null)}
                />
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {([1, 2, 3, 4, 5, 6] as const).map((slot) => {
                  const occupant = myTeamData?.find((s: TeamSlot) => s.slot === slot)?.pokemon;
                  const isOccupied = occupant !== null && occupant !== undefined;
                  return (
                    <button
                      key={slot}
                      type="button"
                      disabled={isSettingTeamSlot}
                      onClick={() => mutateTeamSlot(slot)}
                      title={isOccupied ? `Remplacer ${occupant!.nickname}` : `Assigner au slot ${slot}`}
                      className={`flex h-9 w-9 flex-col items-center justify-center rounded-xl border text-xs font-semibold transition-colors ${
                        isOccupied
                          ? 'border-primary-400/30 bg-primary-500/10 text-primary-500 hover:bg-primary-500/15'
                          : 'border-line-soft bg-raised text-content/40 hover:bg-raised'
                      } disabled:opacity-50`}
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {pokemon.pokemon.description && (
            <div className="bg-secondary-500/10 mb-5 rounded-2xl p-4">
              <p className="text-content/70 text-sm leading-relaxed italic">
                "{pokemon.pokemon.description}"
              </p>
            </div>
          )}

          {/* Relâcher */}
          {showConfirm ? (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4">
              <p className="text-content mb-3 text-sm font-medium">
                Relâcher <strong>{pokemon.nickname}</strong> ? Vous gagnerez{' '}
                <strong>{reward.toLocaleString('fr-FR')} PokéDollars</strong>.
              </p>
              <div className="flex gap-2">
                <CustomButton
                  title="Annuler"
                  color="white"
                  size="sm"
                  rounded="full"
                  onClick={() => setShowConfirm(false)}
                  disabled={isReleasing}
                />
                <CustomButton
                  title="Confirmer"
                  color="secondary"
                  size="sm"
                  rounded="full"
                  loading={isReleasing}
                  onClick={() => release()}
                />
              </div>
            </div>
          ) : (
            <CustomButton
              title="Relâcher"
              color="secondary"
              variant="ghost"
              size="sm"
              rounded="full"
              fullWidth
              onClick={() => setShowConfirm(true)}
            />
          )}
        </div>

        {/* Overlay évolution immédiate (pierre) */}
        {evolutionResult && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-3xl bg-black/80 p-8 text-center">
            <p className="font-lexend mb-2 text-lg font-semibold text-white/70">Quoi ?</p>
            <p className="font-lexend mb-6 text-2xl font-bold text-white">
              {evolutionResult.fromName} évolue&nbsp;!
            </p>
            <div className="mb-6 flex items-center gap-4 text-white">
              <span className="font-lexend text-xl">{evolutionResult.fromName}</span>
              <span className="text-white/50">→</span>
              <span className="font-lexend text-primary-500 text-xl font-bold">{evolutionResult.evolvedName}</span>
            </div>
            {evolutionResult.isNewInPokedex && (
              <p className="font-lexend mb-4 text-sm text-yellow-400">
                📖 Nouveau dans le Pokédex&nbsp;! +{evolutionResult.pokeDollarsEarned.toLocaleString('fr-FR')} PokéDollars
              </p>
            )}
            <CustomButton
              title="Super !"
              color="primary"
              rounded="full"
              onClick={() => { setEvolutionResult(null); onClose(); }}
            />
          </div>
        )}

        {/* Évolution en attente (Super Bonbon) */}
        {pendingEvolution && (
          <EvolutionModal
            evolution={pendingEvolution}
            onDone={() => { setPendingEvolution(undefined); onClose(); }}
          />
        )}

        {/* Nouvelle attaque (Super Bonbon) */}
        {pendingMove && (
          <LearnMoveModal
            ownedPokemonId={pokemon.ownedPokemonId}
            pokemonName={pokemon.pokemon?.realname ?? pokemon.nickname}
            pendingMove={pendingMove}
            currentAttacks={pokemon.attacks}
            onDone={() => { setPendingMove(undefined); onClose(); }}
          />
        )}
      </div>
    </div>
  );
};

export const Pokemons = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState(
    searchParams.get('search') ?? ''
  );
  const [debouncedSearch, setDebouncedSearch] = useState(
    searchParams.get('search') ?? ''
  );
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<'createdAt' | 'level' | 'nickname' | 'rarity' | 'pokedexId'>(
    'createdAt'
  );
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [limit, setLimit] = useState<25 | 50 | 100>(25);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [onlyShiny, setOnlyShiny] = useState(false);
  const [onlyLegendary, setOnlyLegendary] = useState(false);
  const [selected, setSelected] = useState<OwnedPokemon | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (value) {
      setSearchParams({ search: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const { data: pokeStats } = useQuery({
    queryKey: ['mePlayerStats'],
    queryFn: getPokePlayerStats,
    staleTime: 30_000,
  });

  const { data: pokemonsData, isLoading, isError, refetch } = useQuery({
    queryKey: ['pokemons', offset, limit, sortBy, sortOrder, debouncedSearch, onlyFavorites, onlyShiny, onlyLegendary],
    queryFn: () =>
      getOwnedPokemons({
        offset,
        limit,
        sortBy,
        sortOrder,
        search: debouncedSearch || undefined,
        onlyFavorites,
        onlyShiny,
        onlyLegendary,
      }),
    placeholderData: keepPreviousData,
  });

  const { data: myTeam } = useQuery({
    queryKey: ['myTeam'],
    queryFn: getMyTeam,
    staleTime: 30_000,
  });

  const { mutate: toggleFavCard } = useMutation({
    mutationFn: (id: string) => toggleFavoriteService(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pokemons'] }),
  });

  const total = pokemonsData?.total ?? 0;
  const pokemons = pokemonsData?.data ?? [];
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const capturedCount = pokeStats?.pokedexProg ?? 0;
  const progress = Math.round((capturedCount / TOTAL_POKEMON) * 100);

  return (
    <main className="bg-page min-h-screen">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <GenericCard
            title="Pokédex"
            subtitle={`${capturedCount} / ${TOTAL_POKEMON} espèces découvertes`}
          >
            <div className="mt-2">
              <div className="bg-primary-500/20 h-3 overflow-hidden rounded-full">
                <div
                  className="from-primary-500 to-secondary-500 h-3 rounded-full bg-linear-to-r transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-content/60 mt-1.5 text-right text-xs font-semibold">
                {progress}%
              </p>
            </div>
          </GenericCard>

          <GenericCard title="PokéDollars">
            <div className="mt-2 flex items-center gap-3">
              <div className="bg-primary-500/15 flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
                <Coins size={24} className="text-primary-500" />
              </div>
              <div>
                <p className="text-content font-lexend text-2xl font-semibold">
                  {(pokeStats?.pokeDollars ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-content/60 text-xs">disponibles</p>
              </div>
            </div>
          </GenericCard>
        </div>

        {pokeStats && (
          <GenericCard title="Statistiques">
            <div className="mt-2">
              <PlayerStatsPanel stats={pokeStats} />
            </div>
          </GenericCard>
        )}

        {/* Panneau équipe */}
        <GenericCard
          title={`Mon équipe (${myTeam?.filter((s: TeamSlot) => s.pokemon !== null).length ?? 0}/6)`}
        >
          <div className="mt-2 flex gap-2">
            {([1, 2, 3, 4, 5, 6] as const).map((slot) => {
              const entry = myTeam?.find((s: TeamSlot) => s.slot === slot);
              const p = entry?.pokemon ?? null;
              return (
                <button
                  key={slot}
                  type="button"
                  onClick={() => p && setSelected(p)}
                  title={p ? p.nickname : `Slot ${slot} vide`}
                  className={`flex h-14 w-14 flex-col items-center justify-center rounded-xl border transition-all ${
                    p
                      ? 'border-primary-400/30 bg-primary-500/10 hover:border-primary-400 hover:shadow-sm'
                      : 'border-dashed border-line-soft bg-raised'
                  }`}
                >
                  {p ? (
                    <img
                      src={cdnUrl(p.isShiny ? p.pokemon.spriteShiny : p.pokemon.sprite)}
                      alt={p.nickname}
                      className="h-10 w-10 object-contain"
                      onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                    />
                  ) : (
                    <span className="text-lg text-gray-300">?</span>
                  )}
                </button>
              );
            })}
          </div>
        </GenericCard>

        <GenericCard
          title="Mes Pokémon"
          actions={
            <div className="flex gap-2">
              <Link to="/pension">
                <CustomButton
                  title="Pension"
                  color="white"
                  size="sm"
                  rounded="full"
                  icon={<Heart size={14} />}
                />
              </Link>
              <Link to="/inventory">
                <CustomButton
                  title="Inventaire"
                  color="primary"
                  size="sm"
                  rounded="full"
                  icon={<Package size={14} />}
                />
              </Link>
              <Link to="/pokemons/fusion">
                <CustomButton
                  title="Fusion"
                  color="secondary"
                  size="sm"
                  rounded="full"
                  icon={<Zap size={14} />}
                />
              </Link>
              <Link to="/leaderboard">
                <CustomButton
                  title="Classement"
                  color="white"
                  size="sm"
                  rounded="full"
                  icon={<Trophy size={14} />}
                />
              </Link>
            </div>
          }
        >
          <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <form
              role="search"
              className="relative max-w-xs flex-1"
              onSubmit={(e) => e.preventDefault()}
            >
              <Search
                size={15}
                className="text-content/40 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
              />
              <input
                type="text"
                aria-label="Rechercher un Pokémon"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Rechercher un Pokémon..."
                className="text-content placeholder:text-content/40 focus:ring-primary-400 h-10 w-full rounded-xl border border-line-soft bg-raised pr-8 pl-9 text-sm focus:ring-2 focus:outline-none"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="text-content/40 hover:text-content/70 absolute top-1/2 right-2.5 -translate-y-1/2 transition-colors"
                  aria-label="Effacer la recherche"
                >
                  <X size={14} />
                </button>
              )}
            </form>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => { setOnlyFavorites(!onlyFavorites); setOffset(0); }}
                className={`flex h-10 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition-colors ${
                  onlyFavorites
                    ? 'bg-primary-500 text-white'
                    : 'border border-line-soft bg-raised text-content/60 hover:bg-line-soft'
                }`}
              >
                <Star size={13} className={onlyFavorites ? 'fill-white' : ''} />
                Favoris
              </button>
              <button
                type="button"
                onClick={() => { setOnlyShiny(!onlyShiny); setOffset(0); }}
                className={`flex h-10 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition-colors ${
                  onlyShiny
                    ? 'bg-yellow-400 text-white'
                    : 'border border-line-soft bg-raised text-content/60 hover:bg-line-soft'
                }`}
              >
                ✨ Shiny
              </button>
              <button
                type="button"
                onClick={() => { setOnlyLegendary(!onlyLegendary); setOffset(0); }}
                className={`flex h-10 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition-colors ${
                  onlyLegendary
                    ? 'bg-amber-500 text-white'
                    : 'border border-line-soft bg-raised text-content/60 hover:bg-line-soft'
                }`}
              >
                🟨 Légendaire
              </button>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value as typeof sortBy);
                  setOffset(0);
                }}
                className="text-content h-10 rounded-xl border border-line-soft bg-raised px-3 text-sm focus:outline-none"
              >
                <option value="createdAt">Date</option>
                <option value="level">Niveau</option>
                <option value="nickname">Nom</option>
                <option value="rarity">Rareté</option>
                <option value="pokedexId">N° Pokédex</option>
                <option value="ivAverage">IV moyen</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value as typeof sortOrder);
                  setOffset(0);
                }}
                className="text-content h-10 rounded-xl border border-line-soft bg-raised px-3 text-sm focus:outline-none"
              >
                <option value="DESC">↓</option>
                <option value="ASC">↑</option>
              </select>
              <div className="flex gap-1">
                {([25, 50, 100] as const).map((pageSize) => (
                  <button
                    key={pageSize}
                    type="button"
                    onClick={() => { setLimit(pageSize); setOffset(0); }}
                    className={`h-10 rounded-xl px-3 text-sm font-semibold transition-colors ${
                      limit === pageSize
                        ? 'bg-primary-500 text-white'
                        : 'text-content/60 border border-line-soft bg-raised hover:bg-line-soft'
                    }`}
                  >
                    {pageSize}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <p className="text-content/50 mb-4 text-xs">
            {pokemonsData
              ? `${total} Pokémon${total !== 1 ? 's' : ''} trouvé${total !== 1 ? 's' : ''}`
              : '\u00A0'}
          </p>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader size={40} color="#f29602" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-4 py-16">
              <p className="text-content/60 text-sm">Impossible de charger tes Pokémon.</p>
              <button
                onClick={() => refetch()}
                className="text-primary-500 hover:text-primary-600 text-sm font-semibold underline"
              >
                Réessayer
              </button>
            </div>
          ) : pokemons.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <p className="text-6xl">🔍</p>
              <p className="text-content/50 text-sm">
                {debouncedSearch
                  ? `Aucun Pokémon trouvé pour «\u00A0${debouncedSearch}\u00A0»`
                  : 'Aucun Pokémon trouvé'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {pokemons.map((ownedPokemon) => !ownedPokemon.pokemon ? null : (
                <button
                  key={ownedPokemon.ownedPokemonId}
                  onClick={() => setSelected(ownedPokemon)}
                  className="hover:border-primary-300 group relative flex flex-col items-center rounded-2xl border border-transparent bg-surface p-3 shadow-sm transition-all duration-200 hover:shadow-lg"
                >
                  {/* Bouton favori */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleFavCard(ownedPokemon.ownedPokemonId); }}
                    aria-label={ownedPokemon.isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                    className="absolute top-1.5 left-1.5 z-10 p-0.5 text-yellow-400 transition-opacity hover:opacity-80"
                  >
                    <Star size={14} className={ownedPokemon.isFavorite ? 'fill-yellow-400' : 'fill-none stroke-gray-300'} />
                  </button>
                  {/* Shiny + team badges */}
                  <div className="absolute top-1.5 right-1.5 flex flex-col items-end gap-0.5">
                    {ownedPokemon.isShiny && (
                      <span className="bg-primary-500 rounded-full px-1.5 py-0.5 text-[10px] leading-none font-bold text-white">
                        ★
                      </span>
                    )}
                    {ownedPokemon.teamSlot !== null && (
                      <span className="bg-secondary-500 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] leading-none font-bold text-white">
                        <Shield size={8} />
                        {ownedPokemon.teamSlot}
                      </span>
                    )}
                  </div>
                  <div className="bg-primary-500/10 mb-2 flex h-16 w-16 items-center justify-center rounded-xl">
                    <img
                      src={cdnUrl(
                        ownedPokemon.isShiny ? ownedPokemon.pokemon.spriteShiny : ownedPokemon.pokemon.sprite
                      )}
                      alt={ownedPokemon.nickname}
                      loading="lazy"
                      className="h-14 w-14 object-contain transition-transform duration-200 group-hover:scale-110"
                      onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                    />
                  </div>
                  <p className="text-content font-lexend w-full truncate text-center text-sm font-semibold">
                    {ownedPokemon.nickname}
                  </p>
                  <p className="text-content/50 mb-1.5 text-center text-xs">
                    {ownedPokemon.pokemon.realname}
                  </p>
                  <div className="mb-1.5 flex flex-wrap justify-center gap-1">
                    {ownedPokemon.pokemon.types.map((typeName) => (
                      <span
                        key={typeName}
                        className={`${TYPE_COLORS[typeName] ?? 'bg-gray-400'} rounded px-1.5 py-0.5 text-[10px] font-semibold text-white capitalize`}
                      >
                        {typeName}
                      </span>
                    ))}
                  </div>
                  <span className="bg-secondary-500 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white">
                    Nv.&nbsp;{ownedPokemon.level}
                  </span>
                </button>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <CustomButton
                icon={<ChevronLeft size={18} />}
                iconOnly
                color="white"
                rounded="full"
                disabled={offset === 0}
                onClick={() => { setOffset(Math.max(0, offset - limit)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              />
              <span className="text-content font-lexend text-sm font-semibold">
                {currentPage}&nbsp;/&nbsp;{totalPages}
              </span>
              <CustomButton
                icon={<ChevronRight size={18} />}
                iconOnly
                color="white"
                rounded="full"
                disabled={offset + limit >= total}
                onClick={() => { setOffset(offset + limit); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              />
            </div>
          )}
        </GenericCard>
      </div>

      {selected && (
        <PokemonDetailModal pokemon={selected} onClose={() => setSelected(null)} />
      )}
    </main>
  );
};